import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabase';
import { Helmet } from 'react-helmet-async';
import whiteicon from '../../assets/manifestSVG.svg';

export default function SignUpBusiness() {
  const navigate = useNavigate();

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [agreed, setAgreed] = useState(false);
  
  // Status State
  const [errorMsg, setErrorMsg] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
const countries = [
  // North America
  'United States', 'Canada', 'Mexico',
  
  // Europe
  'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Greece',
  
  // Asia-Pacific
  'Japan', 'Australia', 'China', 'India', 'Thailand', 'New Zealand',
  
  // Middle East & Africa
  'United Arab Emirates', 'South Africa', 'Egypt',
  
  // South America
  'Brazil'
];
  const categories = ['Bank', 'Jewelry Store', 'Clothing Store', 'Car Dealership', 'Software', 'Travel Agency', 'Business Services', 'Fitness Services', 'Furniture Store', 'Pet Store', 'Insurance Agency', 'Home Services', 'Education', 'Electronics', 'Cosmetics Store', 'Real Estate'];

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => setCountry(data.country_name))
      .catch(err => console.error('Geolocation error:', err));
  }, []);

  const getRawDomain = (input) => {
    if (!input) return "";
    let domain = input.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].trim();
    const domainParts = domain.split('.');
    if (domainParts.length < 2 || domainParts[domainParts.length - 1].length < 2) return null;
    return domain;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setErrorMsg('Please agree to the Terms & Conditions');
      return;
    }
    
    setErrorMsg('');
    setSuccessMessage('');
    const cleanDomain = getRawDomain(companyWebsite);
    
    if (!cleanDomain) {
      setErrorMsg("Please enter a valid domain (e.g., example.com)");
      return;
    }

    setLoading(true);

    try {
      const { data: existing, error: checkError } = await supabase
        .from('businesses')
        .select('www, isverif')
        .eq('www', cleanDomain)
        .maybeSingle();

      if (checkError) throw new Error("Database connection error.");
      if (existing) throw new Error("This business is already registered. Think someone claimed this incorrectly? Contact support to claim ownership.");

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: `${name} ${lastname}`, 
            full_name: `${name} ${lastname}`, 
            companyWebsite: cleanDomain,
            isBusiness: true,
          }
        }
      });

      if (authError) throw authError;

      const { error: funcError } = await supabase.functions.invoke('register-business', {
        body: { 
          userId: authData.user.id, 
          companyName, 
          domain: cleanDomain,   
          email,
          name, phone, country, category 
        }
      });

      if (funcError) throw new Error("Profile setup failed. Contact support.");

      setSuccessMessage("Verification link sent! Check your business email.");
      setTimeout(() => navigate('/signin'), 5000);

    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen w-full bg-[#f4f4f4] flex items-center justify-center pb-[60px] px-4 py-6 perspective-1000'>
      <Helmet>
        <title>Business Registration | Sitemanifest</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className='w-full lg:max-w-[1000px] mt-[60px] lg:mt-[90px] max-w-[450px] bg-white rounded-2xl shadow-[0_40px_80px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden flex flex-col lg:flex-row transform lg:rotate-y-[-4deg] lg:rotate-x-[2deg] transition-transform duration-700 hover:rotate-0'>
        
        {/* LEFT SIDE (Branding - Marcus V. Testimonial) */}
        <div className='hidden lg:flex lg:w-2/5 bg-[#1C6065] p-10 flex-col justify-between relative overflow-hidden'>
          <div className='absolute inset-0 z-0 opacity-[0.6] scale-150 transform translate-x-10 translate-y-10 select-none pointer-events-none'>
            <svg width="100%" height="100%" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="400" cy="400" r="380" stroke="#ffffff" strokeWidth="1"/>
              <ellipse cx="400" cy="400" rx="380" ry="140" stroke="#ffffff" strokeWidth="1"/>
              <ellipse cx="400" cy="400" rx="140" ry="380" stroke="#ffffff" strokeWidth="1"/>
              <line x1="20" y1="400" x2="780" y2="400" stroke="#ffffff" strokeWidth="1"/>
              <line x1="400" y1="20" x2="400" y2="780" stroke="#ffffff" strokeWidth="1"/>
            </svg>
          </div>
          <div className='relative z-10'>
            <div className='flex flex-row items-center gap-[14px]'>
              <img src={whiteicon} alt="" className="w-7 h-7 object-contain brightness-0 invert" />
              <h2 className='font-staat text-white text-2xl tracking-widest uppercase'>SITEMANIFEST</h2>
            </div>
            <div className='w-8 h-[2px] bg-[#7CD998] mt-1'></div>
          </div>
          <div className='relative z-10 bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-xl shadow-xl'>
            <p className='text-white font-poppins text-sm italic opacity-90'>"Finally a platform where trust is the core feature."</p>
            <div className='mt-4 flex items-center gap-3 border-t border-white/10 pt-3 text-white text-xs font-semibold'>
              <div className='w-8 h-8 rounded-full bg-gradient-to-tr from-[#7CD998] to-white flex items-center justify-center text-[#1C6065]'>M</div>
              <p>Marcus V. <span className='text-[#7CD998] block text-[9px] uppercase font-bold'>Verified User</span></p>
            </div>
          </div>
          <div className='w-8 h-1 rounded-full bg-white relative z-10'></div>
        </div>

        {/* RIGHT SIDE (The Form) */}
        <div className='w-full lg:w-3/5 p-6 sm:p-10 flex flex-col justify-center bg-white relative'>
          
          {/* RESPONSIVE HEADER & SWITCHER */}
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4'>
            <div>
              <h1 className='text-2xl font-bold text-[#1C6065] tracking-tight'>Business</h1>
              <p className='text-gray-400 text-[11px] mt-1'>
                Already a member? <button onClick={() => navigate('/signin')} className='text-[#1E7D6B] font-bold hover:underline'> Log in</button>
              </p>
            </div>
            
            <div className='flex bg-gray-100 p-1 rounded-full self-start sm:self-center'>
              <button onClick={() => navigate('/signup')} className='px-4 py-1.5 text-[9px] font-bold rounded-full text-gray-400 hover:text-[#1C6065]'>PERSONAL</button>
              <button onClick={() => navigate('/business/signup')} className='px-4 py-1.5 text-[9px] font-bold rounded-full bg-white text-[#1C6065] shadow-sm'>BUSINESS</button>
            </div>
          </div>

          {/* DYNAMIC MESSAGE: Only renders if state exists */}
          {(errorMsg || successMessage) && (
            <div className="mb-4 animate-message">
              {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-[11px] font-bold">
                  <span className="w-4 h-4 shrink-0 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]">!</span>
                  {errorMsg}
                </div>
              )}
              {successMessage && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-100 text-[#1C6065] text-[11px] font-bold">
                  <span className="w-4 h-4 shrink-0 rounded-full bg-[#1C6065] text-white flex items-center justify-center text-[10px]">✓</span>
                  {successMessage}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSignUp} className='space-y-3'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>First Name</label>
                <input type="text" placeholder="First" value={name} onChange={(e) => setName(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Last Name</label>
                <input type="text" placeholder="Last" value={lastname} onChange={(e) => setLastname(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Company</label>
                <input type="text" placeholder="Acme Corp" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Website</label>
                <input type="text" placeholder="example.com" value={companyWebsite} onChange={(e) => setCompanyWebsite(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Biz Email</label>
                <input type="email" placeholder="ceo@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Phone</label>
                <input type="tel" placeholder="+1..." value={phone} onChange={(e) => setPhone(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Country</label>
                <select value={country} onChange={e => setCountry(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none appearance-none">
                  <option value="" disabled>Select</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className='space-y-0.5'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none appearance-none">
                  <option value="" disabled>Select</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
            </div>

            <div className='space-y-0.5'>
              <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full bg-gray-50 border border-gray-100 text-sm h-[40px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
            </div>

            <div className='flex items-center gap-3 py-1 cursor-pointer select-none' onClick={() => setAgreed(!agreed)}>
              <div className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all ${agreed ? 'bg-[#1E7D6B] border-[#1E7D6B]' : 'border-gray-200'}`}>
                {agreed && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="4"><path d="M5 13l4 4L19 7"></path></svg>}
              </div>
              <p className='text-[10px] text-gray-500'>Agree to <span className='text-[#1C6065] font-bold underline'>Terms</span></p>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#1C6065] h-[46px] rounded-xl font-bold text-sm text-white shadow-lg hover:bg-[#164d51] transition-all transform active:scale-[0.98] disabled:opacity-50">
              {loading ? 'SYNCING...' : 'REGISTER BUSINESS'}
            </button>
          </form>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .perspective-1000 { perspective: 1200px; }
        .rotate-y-[-4deg] { transform: rotateY(-4deg) rotateX(2deg); }
        .animate-message { animation: slideIn 0.3s ease-out forwards; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}} />
    </div>
  );
}